/**
 * Audit Log Tabs Component
 *
 * Provides tab navigation for switching between Application Logs and Admin Logs.
 * Syncs the active tab with URL query parameters for deep linking.
 */

import { useSearchParams } from 'react-router';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AUDIT_LOG_TAB_LABELS, type AuditLogTab } from '@/types/audit-log';

interface AuditLogTabsProps {
  /**
   * Callback fired when the active tab changes
   * @param tab - The newly selected tab
   */
  onTabChange?: (tab: AuditLogTab) => void;
  /**
   * The default tab to show (defaults to 'application')
   */
  defaultTab?: AuditLogTab;
}

/**
 * Tab navigation component for audit logs
 *
 * Features:
 * - Syncs active tab with URL query parameter (?tab=admin or ?tab=application)
 * - Defaults to 'application' tab if no tab specified
 * - Persists tab selection across page refreshes
 * - Provides callback for parent components to react to tab changes
 */
export function AuditLogTabs({ onTabChange, defaultTab = 'application' }: AuditLogTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL params, fallback to default
  const activeTab = (searchParams.get('tab') as AuditLogTab) || defaultTab;

  /**
   * Handle tab changes by updating URL params and notifying parent
   */
  const handleTabChange = (value: string) => {
    const newTab = value as AuditLogTab;

    // Update URL query params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);

    // Reset to page 1 when switching tabs
    newParams.set('page', '1');

    setSearchParams(newParams);

    // Notify parent component
    onTabChange?.(newTab);
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <TabsList className="grid w-full max-w-md grid-cols-2">
        <TabsTrigger value="application">{AUDIT_LOG_TAB_LABELS.application}</TabsTrigger>
        <TabsTrigger value="admin">{AUDIT_LOG_TAB_LABELS.admin}</TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
